import React, { useLayoutEffect, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import { Pointer } from 'lucide-react';
import './TMFPresentation.css';

gsap.registerPlugin(ScrollTrigger);

const T = '/content/tmf data/';

// ── Data ────────────────────────────────────────────────────────────

const IMPACT_OUTCOMES = [
  {
    tag: 'Methods adoption',
    title: 'Adopted by every partner pantry, and traveling beyond them.',
    body: 'Our three research methods now function as a reusable toolkit that pantries can run themselves. Already shared with the Impact Center Indiana and other teams doing sensitive community research.',
  },
  {
    tag: 'Live at SECS',
    title: 'SECS has already implemented the recommendations.',
    body: 'Multi-channel outreach, trusted-referral networks, and reframed service communication for seven non-food programs are live changes at the pantry today.',
  },
  {
    tag: 'Strategic influence',
    title: '5 personas now sit on workshop tables at Toyota, IU, Purdue, IndyGo, and the regional MPO.',
    body: 'Every AV concept generated in Spring 2026 workshops is grounded in one of five real community situations, not assumed user needs.',
  },
  {
    tag: 'Service concept',
    title: 'A speculative AV service concept, proposed and in review.',
    body: 'A system-level proposal that reframes AVs as social-care infrastructure: ownership, operations, and on-board services designed from the community up.',
  },
];

const METHODS = [
  {
    num: '00',
    name: 'Site Observation',
    stat: '5 hrs · multiple visits',
    body: 'Volunteered alongside SECS staff during live operations. Mapped the floor, the movement patterns, the social dynamics, the moments of dignity.',
    img: `${T}Site Pictures/Site Picture 7070.jpg`,
  },
  {
    num: '01',
    name: 'AI-Moderated Interviews',
    stat: '27 conversations',
    body: 'Audio-only interviews on a tablet in the drive-through. Removed face-to-face pressure around money, employment, and personal struggle.',
    img: `${T}images/ai interview/Image 9065.jpg`,
  },
  {
    num: '02',
    name: 'Sticker Walls',
    stat: '107+ walk-ins',
    body: 'Icon-driven dot survey walls along the pantry queue. Passive opt-in, anonymous, no literacy or language barrier, scales without interrupting service flow.',
    img: `${T}tmf-shortimages/stickerwall.jpeg`,
  },
  {
    num: '03',
    name: 'Insight Box',
    stat: '3 focus groups · 120 min each',
    body: 'Tactile focus groups built around an ideal-meal metaphor. Sensitive topics get discussed safely through food as a shared entry point.',
    img: `${T}tmf-shortimages/insightbox.jpg`,
  },
];

const PERSONAS = [
  { initial: 'A', name: 'Arthur', tag: 'Homebound',              line: "Can't walk to the bus. Hates asking for rides. Slowly losing community." },
  { initial: 'M', name: 'Maria',  tag: 'Volunteer neighbor',     line: 'Picks up food for two other families. Coordinates the block. Burning out.' },
  { initial: 'J', name: 'Joel',   tag: 'Working hungry',         line: 'Earns a wage, supports a large family, still falls short. Wants to upskill but has no bandwidth left after work.' },
  { initial: 'S', name: 'Sarah',  tag: 'Transportation paradox', line: 'Bought a car for work. The car is now the biggest expense in her life.' },
  { initial: 'M', name: 'Maya',   tag: 'No time, no car',        line: 'Single parent, no car, caring for both her child and her mother. Every errand costs time she does not have.' },
];

const DIRECTIONS = [
  { num: '01', title: 'Coordination, not conveyance',        body: "The AV's value is not the ride. It is what the ride makes possible." },
  { num: '02', title: 'Reach the unreachable',               body: 'Extend to people the current system cannot serve: homebound, unaddressed, carless.' },
  { num: '03', title: 'Automate logistics, not relationships', body: 'Automate the effort around the service. Protect the human moment at delivery.' },
  { num: '04', title: 'Flexible mode',                       body: 'Transport food to people, or transport people to food. Both modes, depending on who and when.' },
  { num: '05', title: 'Trust through demonstration',         body: 'Repeated visible operation, human backup, phased rollout. Information campaigns do not move this forward.' },
];

const SYSTEM_COLS = [
  {
    label: 'Access',
    title: 'Book through the pantry, not an app.',
    body: 'Maya books rides using pantry-issued ride credits: no new accounts, no unfamiliar systems. The service is integrated into a network she already trusts, keeping the barrier to entry as low as possible.',
  },
  {
    label: 'The ride',
    title: 'More than a trip to the pantry.',
    body: 'The AV gets Maya to SECS and back reliably. She can carry more groceries than she could on foot. And she gains access to services beyond food that she never knew were available.',
  },
  {
    label: 'The opportunity',
    title: 'Transit time becomes productive time.',
    body: 'During the ride, Maya can attend job coaching, access support services, or plan her next steps. Engaging with those services earns her more ride credits, making the system sustainable, measurable, and self-reinforcing.',
  },
];

const PROTOTYPE_SCREENS = [
  {
    num: '00',
    title: 'The premise',
    body: 'The landing screen sets the frame before Maya’s afternoon starts: a ride that does more than get her somewhere.',
    img: `${T}prototype-screens/01-landing.png`,
  },
  {
    num: '01',
    title: 'The request',
    body: '4:15 PM, shift just ended. Maya requests the AV instead of calculating bus fare against grocery money. It’s already two minutes out.',
    img: `${T}prototype-screens/02-pretrip-request.png`,
  },
  {
    num: '02',
    title: 'One trip, four stops',
    body: 'Work, school, pantry, home. The route folds her whole afternoon into a single ride instead of four separate trips.',
    img: `${T}prototype-screens/03-multistop-routing.png`,
  },
  {
    num: '03',
    title: 'The ride becomes a resource',
    body: 'In transit, the pantry network unlocks job coaching, financial counseling, and health referrals, services she did not know she qualified for.',
    img: `${T}prototype-screens/04-pantry-resource-intro.png`,
  },
  {
    num: '04',
    title: 'Ready when she is',
    body: 'A prompt to start the coaching session surfaces twenty-eight minutes out, offered mid-route, never forced.',
    img: `${T}prototype-screens/06-pantry-arrival.png`,
  },
  {
    num: '05',
    title: 'Coaching, mid-ride',
    body: 'A live resume-review session runs on the in-vehicle screen, synced to her phone and timed against the minutes left to the pantry.',
    img: `${T}prototype-screens/05-job-coaching.png`,
  },
  {
    num: '06',
    title: 'More than a pickup',
    body: 'Transit time becomes browsing time: nearby community services surface on the way, and the pantry stop turns into a place to linger.',
    img: `${T}prototype-screens/07-Community.png`,
  },
  {
    num: '07',
    title: 'The impact, side by side',
    body: 'Two hours of walking and two grocery bags become forty-five minutes and a full week of food, coaching session already finished.',
    img: `${T}prototype-screens/08-impact.png`,
  },
];

const PARTNERS_LOGO = [
  { name: 'Toyota Mobility Foundation',  logo: `${T}tmf logos/TMF Logo.svg` },
  { name: 'Toyota',                       logo: `${T}tmf logos/Toyota Logo.png` },
  { name: 'PIT-UN',                       logo: `${T}tmf logos/PIT UN Logo 300x60.webp` },
  { name: 'Southeast Community Services', logo: `${T}tmf logos/Pantry Southeast Community Services.avif` },
  { name: 'Indy Hunger Network',          logo: `${T}tmf logos/Indy Hunger Network Logo.webp` },
  { name: 'IndyGo',                       logo: `${T}tmf logos/IndyGo Blue Logo.svg` },
  { name: 'IMPO',                         logo: `${T}tmf logos/IMPO Logo Blue.png` },
  { name: 'Purdue Engineering',           logo: `${T}tmf logos/Purdue Engineering Logo.svg` },
  { name: 'Indiana University',           logo: `${T}tmf logos/iu logo.png` },
  { name: 'Butler University',            logo: `${T}tmf logos/butler university.jpg` },
  { name: 'TSSC',                         logo: `${T}tmf logos/TSSC Logo.jpg` },
  { name: 'MPO Indiana',                  logo: `${T}tmf logos/MPO Indiana Logo.png` },
  { name: 'Shepherd Community',           logo: `${T}tmf logos/Shepherd Community Logo.png` },
  { name: 'Common Ground Faith',          logo: `${T}tmf logos/Common Ground Faith Logo.png` },
];

const PARTNERS_TEXT = [
  'Indiana University Indianapolis',
  'Purdue Engineering',
  'IndyGo Transit',
  'Metropolitan Planning Organization',
  'Nine 13 Logistics',
  'Shepherds Community',
  'Indy Hunger Network',
];

const SESSIONS = [
  {
    num: '01',
    title: 'Solution ideation with research teams',
    who: 'Toyota Mobility Foundation · Impact Center · 9/13 Logistics · Purdue Engineering',
    body: 'An intense 90-minute session translating our community findings into service concepts. Mixed teams from Toyota, logistics, and engineering worked directly from the five personas to generate early solution directions.',
    img: `${T}tmf-shortimages/Stakeholder workshops/internals teams.jpg`,
  },
  {
    num: '02',
    title: 'Pantry volunteer voices',
    who: 'Indy Hunger Network · SECS Volunteers · Senior community members',
    body: 'A co-design session with the people who operate the pantries week after week. What does a good delivery look like to someone who has been showing up for years? Their constraints shaped the operations layer of the final concept.',
    img: `${T}tmf-shortimages/Stakeholder workshops/senior volunteers.jpg`,
  },
  {
    num: '03',
    title: 'System-level stakeholder session',
    who: 'IndyGo · Metropolitan Planning Organization · Indy Hunger Network · Shepherd Community · 9/13 Logistics',
    body: 'Infrastructure, policy, and routing in one room. The largest session and the one that produced the most actionable pilot pathway: a micro-transit first-mile connector to SECS and Groceries to Go, named by IndyGo and MPO as the strongest near-term scenario.',
    img: `${T}tmf-shortimages/Stakeholder workshops/system level.jpeg`,
  },
];

// ── Component ────────────────────────────────────────────────────────

interface TMFPresentationProps {
  theme?: string;
}

const TMFPresentation: React.FC<TMFPresentationProps> = ({ theme = 'light' }) => {
  const rootRef = useRef<HTMLDivElement>(null);

  useLayoutEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useLayoutEffect(() => {
    document.body.className = 'light';
    // Restore to the app's real theme state on unmount, not a snapshotted
    // className: with AnimatePresence's exit-transition delay, a DOM
    // snapshot can go stale before cleanup runs and leave body stuck light.
    return () => { document.body.className = theme; };
  }, [theme]);

  useEffect(() => {
    const ctx = gsap.context(() => {

      gsap.from('.tf__hero-video', {
        opacity: 0, duration: 1.6, ease: 'power3.out', delay: 0.1
      });
      gsap.from('.tf__title', {
        y: 32, opacity: 0, duration: 0.9, ease: 'power3.out', delay: 0.25
      });
      gsap.from('.tf__descriptor', {
        y: 22, opacity: 0, duration: 0.8, ease: 'power3.out', delay: 0.4
      });
      gsap.from('.tf__meta-item', {
        y: 16, opacity: 0, duration: 0.65, ease: 'power3.out', stagger: 0.07, delay: 0.55
      });

      gsap.set('.tf-fade-up', { y: 30, opacity: 0 });
      gsap.set('.tf-fade-left', { x: -52, opacity: 0 });
      gsap.set('.tf-fade-right', { x: 52, opacity: 0 });
      gsap.set('.tf-fade-scale', { scale: 0.93, opacity: 0 });

      ScrollTrigger.batch('.tf-fade-up', {
        onEnter: (els) => gsap.to(els, { y: 0, opacity: 1, stagger: 0.09, duration: 0.85, ease: 'power3.out' }),
        start: 'top 90%',
        once: true,
      });
      ScrollTrigger.batch('.tf-fade-left', {
        onEnter: (els) => gsap.to(els, { x: 0, opacity: 1, stagger: 0.08, duration: 0.95, ease: 'power3.out' }),
        start: 'top 88%',
        once: true,
      });
      ScrollTrigger.batch('.tf-fade-right', {
        onEnter: (els) => gsap.to(els, { x: 0, opacity: 1, stagger: 0.08, duration: 0.95, ease: 'power3.out' }),
        start: 'top 88%',
        once: true,
      });
      ScrollTrigger.batch('.tf-fade-scale', {
        onEnter: (els) => gsap.to(els, { scale: 1, opacity: 1, stagger: 0.07, duration: 1, ease: 'power3.out' }),
        start: 'top 88%',
        once: true,
      });

      // One-time nudge: cursor glides toward the live prototype's corner,
      // taps, calls out that it's interactive, then clears itself.
      const hintEl = rootRef.current?.querySelector('.tf__interact-hint');
      if (hintEl) {
        const hand = hintEl.querySelector('.tf__interact-hand');
        const tooltip = hintEl.querySelector('.tf__interact-tooltip');
        const hintTl = gsap.timeline({ paused: true });
        hintTl.set(hintEl, { opacity: 0, x: 30, y: 30, scale: 0.85 });
        hintTl.set(tooltip, { opacity: 0, y: 6 });
        hintTl.to(hintEl, { opacity: 1, x: 0, y: 0, scale: 1, duration: 0.7, ease: 'power3.out' });
        hintTl.to(hand, { scale: 0.82, duration: 0.12, ease: 'power1.inOut', yoyo: true, repeat: 1 }, '-=0.05');
        hintTl.to(tooltip, { opacity: 1, y: 0, duration: 0.4, ease: 'power2.out' }, '-=0.35');
        hintTl.to(hintEl, { opacity: 0, duration: 0.6, ease: 'power2.in' }, '+=1.9');

        ScrollTrigger.create({
          trigger: '.tf__prototype-frame',
          start: 'top 75%',
          once: true,
          onEnter: () => hintTl.play(),
        });
      }

    }, rootRef);

    return () => ctx.revert();
  }, []);

  return (
    <div className="tf" ref={rootRef}>

      {/* ── Hero ─────────────────────────────────────────────────── */}
      <section className="tf__hero">
        <video
          className="tf__hero-video"
          src="https://pub-8a575460bea442e2a9d816d772948be2.r2.dev/tmf-card.mp4"
          autoPlay
          muted
          loop
          playsInline
          onLoadedMetadata={(e) => { (e.target as HTMLVideoElement).currentTime = 10; }}
        />
        <div className="tf__hero-overlay" />
      </section>

      <div className="tf__container">

        {/* ── Header ───────────────────────────────────────────────── */}
        <header className="tf__header">
          <h1 className="tf__title">Mobility as a Social Service</h1>
          <p className="tf__descriptor">
            Developed as part of the Mobility as a Social Service Project, Indiana University Indianapolis, 2025–2026. A year-long community-grounded research and service design capstone exploring how autonomous vehicles can expand access to food and social services in Indianapolis, funded by Public Interest Technology University Network (PIT-UN).
          </p>
          <div className="tf__meta">
            {[
              { label: 'Type', value: 'Research + Service Design' },
              { label: 'Year', value: '2025–26' },
              { label: 'Role', value: 'UX Designer · Team of 5' },
              { label: 'Partners', value: 'TMF, SECS, Gleaners, IU' },
            ].map(({ label, value }) => (
              <div className="tf__meta-item" key={label}>
                <div className="tf__meta-label">{label}</div>
                <div className="tf__meta-value">{value}</div>
              </div>
            ))}
          </div>
        </header>

        {/* ── Narrative intro ───────────────────────────────────────── */}
        <section className="tf__section">
          <div className="tf__section-label tf-fade-up">the story</div>
          <div className="tf__split">
            <p className="tf__statement tf-fade-up">
              A food pantry. An AV. And the gap between a good idea and the people it's meant to serve.
            </p>
            <div className="tf__narrative-beats tf-fade-up">
              <div className="tf__narrative-beat">
                <span className="tf__narrative-num">01</span>
                <p className="tf__body">Food pantries serve people who can't always afford groceries. 1 in 8 Indianapolis residents experience food insecurity, but getting to the pantry is only part of the challenge. Sustaining is harder.</p>
              </div>
              <div className="tf__narrative-beat">
                <span className="tf__narrative-num">02</span>
                <p className="tf__body">Toyota Mobility Foundation asked: could an autonomous vehicle expand access? But you can't drop an AV in a neighborhood and call it a day. You need to understand the people first.</p>
              </div>
              <div className="tf__narrative-beat">
                <span className="tf__narrative-num">03</span>
                <p className="tf__body">We partnered with Southeast Community Services, one of Indy's most active pantries. Even the pantry didn't fully know its clients. Visitors are short on time, navigating language barriers, going through a lot. They want their food and to leave.</p>
              </div>
              <div className="tf__narrative-beat">
                <span className="tf__narrative-num">04</span>
                <p className="tf__body">Standard pantry data is a 4–5 question government survey. It captures compliance, not lives. So we designed three new research methods from scratch, built for sensitive populations inside live pantry operations.</p>
              </div>
            </div>
          </div>
        </section>

        {/* ── TL;DR ────────────────────────────────────────────────── */}
        <section className="tf__section">
          <div className="tf__section-label tf-fade-up">tl;dr</div>
          <div className="tf__split">
            <p className="tf__statement tf-fade-up">
              We spent a year inside the community. This is what changed.
            </p>
            <p className="tf__body tf-fade-up">
              Communities already run on informal rides, carpools, and proxy pickups. The design challenge was not to build a better vehicle. It was to understand what coordination actually costs a working family, then design infrastructure that reduces that cost.
            </p>
          </div>

          {/* Impact outcomes, right after TL;DR */}
          <div className="tf__outcomes-grid">
            {IMPACT_OUTCOMES.map((o) => (
              <div className="tf__outcome-card tf-fade-up" key={o.tag}>
                <span className="tf__outcome-tag">{o.tag}</span>
                <h3 className="tf__outcome-title">{o.title}</h3>
                <p className="tf__outcome-body">{o.body}</p>
              </div>
            ))}
          </div>

          {/* Stats strip */}
          <div className="tf__stat-strip">
            {[
              { num: '3',    label: 'novel research methods' },
              { num: '107+', label: 'community participants' },
              { num: '5',    label: 'personas' },
              { num: '9',    label: 'cross-functional partners' },
            ].map(({ num, label }) => (
              <div className="tf__stat-item tf-fade-up" key={label}>
                <span className="tf__stat-num">{num}</span>
                <span className="tf__stat-label">{label}</span>
              </div>
            ))}
          </div>

          {/* Partner logos */}
          <div className="tf__partner-logos-strip">
            {PARTNERS_LOGO.map((p) => (
              <div className="tf__partner-logo-cell tf-fade-scale" key={p.name} title={p.name}>
                <img src={p.logo} alt={p.name} />
              </div>
            ))}
          </div>
        </section>

        {/* ── Research intro ────────────────────────────────────────── */}
        <section className="tf__section tf__section--no-bottom">
          <div className="tf__section-label tf-fade-up">research approach</div>
          <div className="tf__split">
            <p className="tf__statement tf-fade-up">
              Community research with sensitive populations usually fails two ways: extractive and fast, or careful but tiny.
            </p>
            <p className="tf__body tf-fade-up">
              We designed a small, dignity-centered method kit that worked inside live pantry operations, across literacy levels and languages, with consent at every step. Three methods. Every method was adopted by every partner pantry we worked with.
            </p>
          </div>
        </section>

      </div>

      <div className="tf__container">

        {/* ── Method cards ─────────────────────────────────────────── */}
        <div className="tf__methods-grid">
          {METHODS.map((m) => (
            <div className="tf__method-card tf-fade-scale" key={m.num}>
              <div className="tf__method-photo">
                <img src={m.img} alt={m.name} />
              </div>
              <div className="tf__method-body">
                <span className="tf__method-num">{m.num}</span>
                <h3 className="tf__method-name">{m.name}</h3>
                <span className="tf__method-stat">{m.stat}</span>
                <p className="tf__body-small">{m.body}</p>
              </div>
            </div>
          ))}
        </div>

        {/* ── Personas ─────────────────────────────────────────────── */}
        <section className="tf__section">
          <div className="tf__section-label tf-fade-up">the personas</div>
          <div className="tf__split">
            <p className="tf__statement tf-fade-up">
              Five real people, built from 107 real conversations.
            </p>
            <p className="tf__body tf-fade-up">
              107 participants generated 300+ code excerpts, which became 12 themes, then 2 activity models, then 5 specific people a stakeholder can design for. The persona is the bridge between community voice and system-level decision.
            </p>
          </div>

          {/* Funnel */}
          <div className="tf__funnel tf-fade-up">
            {[
              { num: '107+', label: 'Participants' },
              { num: '300+', label: 'Code excerpts' },
              { num: '12',   label: 'Themes' },
              { num: '2',    label: 'Activity models' },
              { num: '5',    label: 'Personas' },
            ].map((step, i, arr) => (
              <React.Fragment key={step.label}>
                <div className="tf__funnel-step">
                  <span className="tf__funnel-num">{step.num}</span>
                  <span className="tf__funnel-label">{step.label}</span>
                </div>
                {i < arr.length - 1 && <span className="tf__funnel-arrow" aria-hidden>→</span>}
              </React.Fragment>
            ))}
          </div>
        </section>

      </div>

      {/* ── Research photo strip full bleed ───────────────────── */}
      <div className="tf__photo-strip">
        {[
          `${T}tmf-shortimages/research bleed/IMG 2392 from designer.jpg`,
          `${T}tmf-shortimages/research bleed/HEIC to JPG 0244 from designer.jpg`,
          `${T}tmf-shortimages/research bleed/HEIC to JPG Image from Designer.jpg`,
          `${T}tmf-shortimages/research bleed/IMG 2394 Designer Human First.jpg`,
        ].map((src, i) => (
          <img key={i} className="tf__photo-strip-img" src={src} alt={`Research pilot ${i + 1}`} />
        ))}
      </div>

      <div className="tf__container">

        {/* Persona cards */}
        <div className="tf__personas tf__personas--section">
          {PERSONAS.map((p) => (
            <div className="tf__persona tf-fade-up" key={p.name + p.tag}>
              <div className="tf__persona-avatar">{p.initial}</div>
              <span className="tf__persona-tag">{p.tag}</span>
              <h3 className="tf__persona-name">{p.name}</h3>
              <p className="tf__persona-line">{p.line}</p>
            </div>
          ))}
        </div>

        {/* ── Ideation sessions ─────────────────────────────────────── */}
        <section className="tf__section tf__section--no-bottom">
          <div className="tf__section-label tf-fade-up">ideation sessions</div>
          <div className="tf__split">
            <p className="tf__statement tf-fade-up">
              We didn't present findings. We put personas on the table and designed alongside the people who will run this.
            </p>
            <p className="tf__body tf-fade-up">
              Three 90-minute co-design sessions, each with a different set of stakeholders. Purdue engineering teams, pantry volunteers, transit planners, and logistics operators all in different rooms, all working from the same five personas. The concept emerged from the intersection.
            </p>
          </div>
        </section>

      </div>

      {/* ── Session cards full bleed ──────────────────────────────── */}
      <div className="tf__sessions-grid">
        {SESSIONS.map((s) => (
          <div className="tf__session-card tf-fade-up" key={s.num}>
            <div className="tf__session-img">
              {s.img ? (
                <img src={s.img} alt={s.title} />
              ) : (
                <div className="tf__session-placeholder" />
              )}
            </div>
            <div className="tf__session-body">
              <span className="tf__session-num">{s.num}</span>
              <h3 className="tf__session-title">{s.title}</h3>
              <span className="tf__session-who">{s.who}</span>
              <p className="tf__body-small">{s.body}</p>
            </div>
          </div>
        ))}
      </div>

      <div className="tf__container">

        {/* ── Narrowing the focus ───────────────────────────────────── */}
        <section className="tf__section">
          <div className="tf__section-label tf-fade-up">narrowing the focus</div>
          <div className="tf__split">
            <p className="tf__statement tf-fade-up">
              We went broad to understand the full space. Then we made a deliberate choice.
            </p>
            <div>
              <p className="tf__body tf-fade-up">
                In each session, participants mapped challenges in the current system, then ideated AV-based solutions for a specific persona. They built out one chosen concept, presented it to the group, and the discussion always landed in the same place: what would have to change in the existing system for this to actually work?
              </p>
              <p className="tf__body tf-fade-up" style={{ marginTop: '1rem' }}>
                All three sessions were analyzed and synthesized to identify AV opportunities, system challenges, and design guidelines across the broader food pantry ecosystem. We documented the full landscape in our report. But this is a one-year capstone, and we could not solve everything we found.
              </p>
              <p className="tf__body tf-fade-up" style={{ marginTop: '1rem' }}>
                So we chose to focus on Maya. Her situation surfaced across 70% of participants: caregiving responsibilities, no transportation, constant time pressure, and the difficulty of planning life around scarcity. She was not the edge case. She was the pattern.
              </p>
            </div>
          </div>
          <div className="tf__narrowing-video tf-fade-up">
            <video className="tf__narrowing-video-el" autoPlay muted loop playsInline>
              <source src="https://pub-8a575460bea442e2a9d816d772948be2.r2.dev/tmf-figjam.mp4" type="video/mp4" />
            </video>
            <p className="tf__body-small tf__narrowing-video-caption">Synthesizing 300+ code excerpts into themes, live in FigJam.</p>
          </div>

          <div className="tf__focus-question tf-fade-up">
            <span className="tf__focus-label">the central question</span>
            <p className="tf__focus-text">How might we design an autonomous vehicle service that connects Maya to the food pantry and to its extended services?</p>
          </div>
        </section>

      </div>

      {/* ── Core finding callout ──────────────────────────────────── */}
      <section className="tf__finding-callout">
        <div className="tf__container">
          <div className="tf__split">
            <p className="tf__section-label tf-fade-up" style={{ color: 'rgba(255,255,255,0.4)' }}>the core finding</p>
            <div>
              <p className="tf__finding-quote tf-fade-up">
                Transportation is not a mobility problem.<br />
                It is a coordination problem.
              </p>
              <p className="tf__body tf-fade-up" style={{ color: 'rgba(255,255,255,0.6)', marginTop: '1.25rem' }}>
                It determines what else becomes possible inside the bounded time and money of a working family. Fix it, and the others become navigable. Ignore it, and every other intervention bottlenecks.
              </p>
            </div>
          </div>
        </div>
      </section>

      <div className="tf__container">

        {/* ── The Concept ──────────────────────────────────────────── */}
        <section className="tf__section">
          <div className="tf__section-label tf-fade-up">the concept</div>
          <div className="tf__split">
            <p className="tf__statement tf-fade-up">
              What if mobility wasn't just transportation? What if it could become a bridge to social services?
            </p>
            <p className="tf__body tf-fade-up">
              The workshops and Maya's situation made the solution goal clear. The problem was not distance. It was that everything Maya needs (the pantry, the job coach, the support services) exists, but is unreachable within the constraints of her day. Five design directions held across every session and shaped the solution.
            </p>
          </div>
          <div className="tf__directions-strip">
            {DIRECTIONS.map((d) => (
              <div className="tf__direction tf-fade-up" key={d.num}>
                <span className="tf__direction-num">{d.num}</span>
                <h3 className="tf__direction-title">{d.title}</h3>
                <p className="tf__body-small">{d.body}</p>
              </div>
            ))}
          </div>
        </section>

        {/* ── The System ───────────────────────────────────────────── */}
        <section className="tf__section">
          <div className="tf__section-label tf-fade-up">the system</div>
          <div className="tf__split">
            <p className="tf__statement tf-fade-up">
              Our service concept is an on-demand AV connected to the pantry system.
            </p>
            <p className="tf__body tf-fade-up">
              The design is built around three interlocking layers: how Maya accesses the service, what the ride itself makes possible, and how engagement creates a feedback loop that sustains the system over time.
            </p>
          </div>
          <div className="tf__system-cols">
            {SYSTEM_COLS.map((c, i) => (
              <div className="tf__system-col tf-fade-up" key={c.label}>
                <span className="tf__system-num">{String(i + 1).padStart(2, '0')} · {c.label}</span>
                <h3 className="tf__system-title">{c.title}</h3>
                <p className="tf__body">{c.body}</p>
              </div>
            ))}
          </div>

          {/* ── Live prototype ─────────────────────────────────────── */}
          <p className="tf__body tf-fade-up" style={{ marginTop: '3rem' }}>
            The concept above is built and deployed. Scroll through it live, or step through Maya's afternoon in the screens below.
          </p>
          <div className="tf__prototype-frame tf-fade-up">
            <div className="tf__prototype-bar">
              <span className="tf__prototype-dot" />
              <span className="tf__prototype-dot" />
              <span className="tf__prototype-dot" />
              <span className="tf__prototype-url">mobility-as-social-service.vercel.app</span>
              <a
                href="https://mobility-as-social-service.vercel.app"
                target="_blank"
                rel="noopener noreferrer"
                className="tf__prototype-open"
              >
                Open live ↗
              </a>
            </div>
            <iframe
              className="tf__prototype-iframe"
              src="https://mobility-as-social-service.vercel.app"
              title="Mobility as a Social Service: live prototype"
              loading="lazy"
            />
            <div className="tf__interact-hint" aria-hidden="true">
              <span className="tf__interact-tooltip">You can interact with it</span>
              <Pointer className="tf__interact-hand" size={22} strokeWidth={2} />
            </div>
          </div>

          <p className="tf__body-small tf__prototype-reel-label tf-fade-up">Maya's afternoon, screen by screen</p>
          <div className="tf__prototype-reel tf-fade-up">
            {PROTOTYPE_SCREENS.map((s) => (
              <div className="tf__prototype-reel-card" key={s.num}>
                <div className="tf__prototype-reel-img">
                  <span className="tf__prototype-reel-num">{s.num}</span>
                  <img src={s.img} alt={s.title} loading="lazy" />
                </div>
                <div className="tf__prototype-reel-body">
                  <h3 className="tf__method-name">{s.title}</h3>
                  <p className="tf__body-small">{s.body}</p>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* ── Reflections ──────────────────────────────────────────── */}
        <section className="tf__section">
          <div className="tf__section-label tf-fade-up">reflections</div>
          <div className="tf__split">
            <p className="tf__statement tf-fade-up">
              What designing for people who can't afford to be designed around taught me.
            </p>
            <div>
              {[
                {
                  num: '01',
                  text: 'The method is the advocacy. Dignity-centered research is not softer research. It is the only research that reaches the people most excluded from existing systems.',
                },
                {
                  num: '02',
                  text: '107+ participants and 300+ code excerpts are nothing until they become five specific people a stakeholder can design for. The persona is the bridge between community voice and system-level decision.',
                },
                {
                  num: '03',
                  text: 'Technology is never the answer to a social problem. Framing it as infrastructure, shaped by ownership, operations, and service, changes what the technology is allowed to become.',
                },
              ].map(({ num, text }) => (
                <div className="tf__reflect-item tf-fade-up" key={num}>
                  <span className="tf__reflect-num">{num}</span>
                  <p className="tf__body">{text}</p>
                </div>
              ))}
              <div className="tf__status tf-fade-up">
                <div className="tf__status-row">
                  <span className="tf__status-label">Status</span>
                  <span className="tf__status-value">Capstone presentation · May 2026</span>
                </div>
                <div className="tf__status-row">
                  <span className="tf__status-label">Publication</span>
                  <span className="tf__status-value">Academic paper in progress</span>
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* ── CTA ──────────────────────────────────────────────────── */}
        <div className="tf__cta tf-fade-up">
          <p className="tf__cta-label">This was the short cut</p>
          <h3 className="tf__cta-title">Want to see how the research was actually done?</h3>
          <Link to="/projects/tmf/full" className="tf__cta-link">
            Read the full case study <span aria-hidden>→</span>
          </Link>
        </div>

      </div>

    </div>
  );
};

export default TMFPresentation;
